// external
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { Badge, Divider, IconButton, List, Box } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';

// internal
import './coe-navbar.component.css';
import logo from '../../assets/logo512.png';
import { User } from '../../models/user.models';
import COENavbarLink from '../general/coe-navbar-link/coe-navbar-link.component';
import NotificationsLink from '../links/notifications-link/notifications.link';
import TeamsLink from '../links/teams-link/teams.link';
import { palette } from '../../theme';
import useService from '../../hooks/service.hook';
import ChatService from '../../services/chat.service';
import { UserAvatar } from '../user-avatar/user-avatar.component';
import { REACT_APP_BUILD_ENV } from '../../constants/server.constants';
import { beginSidebarTransition } from '../../stores/sidebar-transition.store';
import { clearHomeScheduleCache } from '../../helpers/cache.helpers';

const UNREAD_POLL_INTERVAL = 30_000;

const navbarDividerSx = { mx: 2, my: 0.5, borderColor: 'var(--color-divider)' };

const CoeNavbar: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(!window.localStorage.getItem('sidedrawer'));
  const [totalUnread, setTotalUnread] = useState(0);
  const user: User = JSON.parse(window.localStorage.getItem('user') as string);

  const chatService = useService(ChatService);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(() => {
    chatService
      .getUnreadCounts()
      .then((response: any) => {
        setTotalUnread(response.data.data?.total ?? 0);
      })
      .catch(() => undefined);
  }, [chatService]);

  useEffect(() => {
    if (REACT_APP_BUILD_ENV === 'dev') return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, UNREAD_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleLogout = () => {
    window.localStorage.removeItem('user');
    clearHomeScheduleCache();
    navigate('/login');
  };

  const handleChangeSidebar = () => {
    beginSidebarTransition();
    sidebarOpen ? window.localStorage.setItem('sidedrawer', 'closed') : window.localStorage.removeItem('sidedrawer');
    document.getElementById('page')?.setAttribute('class', sidebarOpen ? 'page-full' : 'page');
    setSidebarOpen(!sidebarOpen);
  };

  const isTabSelected = (path: string) => {
    return (
      location.pathname === path ||
      (path === '/profile' && (location.pathname === '/profile' || location.pathname.startsWith('/profile/'))) ||
      (location.pathname === '/' && path === '/home') ||
      (location.pathname.includes('/home/') && path.includes('/home')) ||
      (location.pathname.includes('/teams/') && path.includes('/teams')) ||
      (location.pathname === '/chat' && path === '/chat') ||
      (location.pathname.includes('/analytics/') && path.includes('/analytics'))
    );
  };

  const iconColor = (active: boolean) => (active ? palette.primary.main : palette.text.primary);

  return (
    <>
      <div
        className={sidebarOpen ? 'navbar-title open' : 'navbar-title'}
        style={{ backgroundColor: palette.background.subtle }}
      >
        <img src={logo} />
        <div>
          <p>CoeLab</p>
        </div>
      </div>
      <nav className={sidebarOpen ? 'navbar open' : 'navbar'} style={{ backgroundColor: palette.background.subtle }}>
        <ul>
          <Box
            sx={{
              maxHeight: '70vh',
              overflowY: 'auto',
              scrollbarWidth: 'none', // Firefox
              '&::-webkit-scrollbar': {
                display: 'none', // Chrome, Safari, Edge
              },
            }}
          >
            <List sx={{ width: '100%' }} component="nav" style={{ color: palette.text.primary, paddingTop: 0 }}>
              <Divider sx={{ ...navbarDividerSx, my: 0 }} />
              <COENavbarLink
                title="Schedule"
                isActive={isTabSelected('/home')}
                collapsed={!sidebarOpen}
                icon={<CalendarTodayOutlined sx={{ fontSize: 20, color: iconColor(isTabSelected('/home')) }} />}
                onClick={() => navigate('/home')}
              />
              <COENavbarLink
                title="Training Log"
                isActive={isTabSelected('/analytics')}
                collapsed={!sidebarOpen}
                icon={<TrendingUpIcon sx={{ fontSize: 20, color: iconColor(isTabSelected('/analytics')) }} />}
                onClick={() => navigate('/analytics')}
              />
              <COENavbarLink
                title="Equipment"
                isActive={isTabSelected('/equipment')}
                collapsed={!sidebarOpen}
                icon={<LocalOfferOutlined sx={{ fontSize: 20, color: iconColor(isTabSelected('/equipment')) }} />}
                onClick={() => navigate('/equipment')}
              />
              <Divider sx={navbarDividerSx} />
              <TeamsLink isActive={isTabSelected('/teams')} sidebarOpen={sidebarOpen} />
              <Divider sx={navbarDividerSx} />
              <COENavbarLink
                title="Chat"
                isActive={isTabSelected('/chat')}
                collapsed={!sidebarOpen}
                icon={
                  <Badge badgeContent={totalUnread} color="primary" max={99}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: iconColor(isTabSelected('/chat')) }} />
                  </Badge>
                }
                onClick={() => navigate('/chat')}
              />
              <COENavbarLink
                title="Profile"
                isActive={isTabSelected('/profile')}
                collapsed={!sidebarOpen}
                icon={<PersonOutline sx={{ fontSize: 20, color: iconColor(isTabSelected('/profile')) }} />}
                onClick={() => navigate('/profile')}
              />
              <NotificationsLink sidebarOpen={sidebarOpen} />
              <COENavbarLink
                title="Logout"
                isActive={false}
                collapsed={!sidebarOpen}
                icon={<LogoutIcon sx={{ fontSize: 20, color: palette.text.primary }} />}
                onClick={handleLogout}
              />
            </List>
          </Box>

          {/* start of bottom menu item */}
          <IconButton
            onClick={handleChangeSidebar}
            style={{
              position: 'fixed',
              bottom: 51,
              left: 3,
            }}
            disableRipple
          >
            {sidebarOpen ? (
              <KeyboardDoubleArrowLeftIcon style={{ color: palette.primary.main, width: 30, height: 30 }} />
            ) : (
              <KeyboardDoubleArrowRightIcon style={{ color: palette.primary.main, width: 30, height: 30 }} />
            )}
          </IconButton>
          <div
            className={sidebarOpen ? 'navbar-footer open' : 'navbar-footer'}
            style={{ backgroundColor: palette.background.subtle }}
          >
            <Divider sx={{ ...navbarDividerSx, my: 0 }} />
            <div className="navbar-profile-container" style={!sidebarOpen ? { justifyContent: 'center' } : undefined}>
              <UserAvatar
                src={user.profileImage}
                firstname={user.firstName}
                lastname={user.lastName}
                userId={user.id}
                size={35}
                fontSize="0.85rem"
                sx={{
                  bgcolor: 'orange',
                  margin: sidebarOpen ? 1.3 : 0,
                  marginRight: sidebarOpen ? 1.3 : 0,
                  height: 35,
                  width: 35,
                }}
              />
              <div
                style={{
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  paddingRight: 8,
                }}
              >
                <p
                  style={{
                    display: !sidebarOpen ? 'none' : 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'auto',
                    textOverflow: 'ellipsis',
                  }}
                >{`${user.firstName} ${user.lastName}`}</p>
              </div>
            </div>
          </div>
        </ul>
      </nav>
    </>
  );
};

export default CoeNavbar;
